import { ArrowRightIcon } from '@radix-ui/react-icons';
import {
  Button,
  Card,
  Flex,
  Progress,
  ScrollArea,
  Table,
  Text,
} from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { getHitterOption } from 'components/common/hitters/search-hitter';
import { HittersContext } from 'contexts/hitters.context';
import { t } from 'i18next';
import { HitterDataSource } from 'lib_ts/enums/hitters.enums';
import { PLAYER_LEVELS_DICT } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { IMergeHitterSuggestion } from 'lib_ts/interfaces/i-hitter';
import { clone, first, isNil } from 'lodash';
import { FC, useContext, useMemo, useState } from 'react';
import { HittersService } from 'services/hitters.service';

const BULK_ID = '--bulk--';

interface IProps {
  suggestions: IMergeHitterSuggestion[];
}

interface IMergeModel {
  sourceID: string;
  selectedID: string | undefined;
  merged: boolean;
}

export const HitterLibraryDuplicateDialog: FC<IProps> = (props) => {
  const { hitters, setLastChanged } = useContext(HittersContext);

  const apiHitterOptions = useMemo(
    () =>
      hitters
        .filter((h) => h.source === HitterDataSource.StatsApi)
        .map((h) => {
          const levelLabel = PLAYER_LEVELS_DICT[h.level]?.label;
          return getHitterOption(
            h,
            levelLabel ? `${t('common.level')}: ${t(levelLabel)}` : 'No Level'
          );
        }),
    [hitters]
  );

  const [open, setOpen] = useState(true);
  const [anyChanges, setAnyChanges] = useState(false);
  const [loadingId, setLoadingId] = useState<string>();

  const [progress, setProgress] = useState<number>(-1);

  // key: source hitter ID, value: suggested/selected hitter ID
  const [selections, setSelections] = useState(() => {
    const output: DictOf<IMergeModel> = {};

    props.suggestions.forEach((m) => {
      output[m.source._id] = {
        sourceID: m.source._id,
        selectedID: first(m.suggestions)?._id,
        merged: false,
      };
    });

    return output;
  });

  const pending = useMemo(
    () =>
      Object.values(selections).filter(
        (m) => m && !m.merged && m.selectedID
      ) as IMergeModel[],
    [selections]
  );

  const applyMerge = async (model: IMergeModel): Promise<boolean> => {
    try {
      if (!model.selectedID) {
        throw new Error(`Cannot merge ${model.sourceID} w/o a selected ID`);
      }

      // session data will be reassigned from selection hitter to source hitter
      const reassignSuccess = await HittersService.getInstance().updateHits({
        filter: {
          hitterID: model.selectedID,
          session: null,
          hitIDs: null,
        },
        model: {
          hitterID: model.sourceID,
        },
      });

      if (!reassignSuccess) {
        throw new Error(
          `Failed to reassign hits from ${model.selectedID} to ${model.sourceID}`
        );
      }

      const selectedHitter = hitters.find((h) => h._id === model.selectedID);

      if (!selectedHitter) {
        throw new Error(
          `Cannot find hitter ${model.selectedID} for merging into ${model.sourceID}`
        );
      }

      if (isNil(selectedHitter.playerPk)) {
        throw new Error(
          `Hitter ${model.selectedID} does not have any playerPk for merging into ${model.sourceID}`
        );
      }

      if (isNil(selectedHitter.statsApiData)) {
        throw new Error(
          `Hitter ${model.selectedID} does not have any Stats API data for merging into ${model.sourceID}`
        );
      }

      // * IMPORTANT: do not use the update or delete fns from HittersContext b/c we don't want the UI to update until after we close the dialog

      // source hitter will be updated w/ everything from the selection hitter
      const updateSuccess = await HittersService.getInstance().updateHitter({
        _id: model.sourceID,
        playerPk: selectedHitter.playerPk,
        statsApiData: selectedHitter.statsApiData,
        name: selectedHitter.name,
        level: selectedHitter.level,
        status: selectedHitter.status,
        source: selectedHitter.source,
        side: selectedHitter.side,
      });

      if (!updateSuccess) {
        throw new Error(`Failed to update existing hitter ${model.sourceID}`);
      }

      setAnyChanges(true);

      // selection hitter will be marked as deleted
      const deleteSuccess = await HittersService.getInstance().deleteHitters([
        model.selectedID,
      ]);

      if (!deleteSuccess) {
        throw new Error(`Failed to delete hitter ${model.selectedID}`);
      }

      setAnyChanges(true);

      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  return (
    <ErrorBoundary identifier="HitterLibraryDuplicateDialog">
      {open && (
        <CommonDialog
          identifier="HitterLibraryDuplicateDialog"
          title="hitters.manage-duplicate-hitters"
          width={RADIX.DIALOG.WIDTH.MD}
          description={t('hitters.manage-duplicate-hitters-msg').toString()}
          content={
            <Flex direction="column" gap="4">
              <Card
                style={{ padding: '2px' }} // the padding allows for the table's border to be visible
              >
                <ScrollArea scrollbars="vertical">
                  <Table.Root
                    style={{ minHeight: '300px', maxHeight: '400px' }}
                  >
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeaderCell width="240px">
                          {t('hitters.existing-hitter')}
                        </Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell>&nbsp;</Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell width="240px">
                          {t('hitters.stats-api-hitter')}
                        </Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell>&nbsp;</Table.ColumnHeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {props.suggestions.map((m) => {
                        const model = selections[m.source._id];
                        const selectedID = model?.selectedID;

                        const loading =
                          loadingId === BULK_ID || loadingId === m.source._id;

                        return (
                          <Table.Row key={m.source._id}>
                            <Table.Cell>
                              <Text>{m.source.name}</Text>
                            </Table.Cell>
                            <Table.Cell>
                              <ArrowRightIcon />
                            </Table.Cell>
                            <Table.Cell>
                              <CommonSearchInput
                                id={`select-${m.source._id}`}
                                values={selectedID ? [selectedID] : []}
                                placeholder="(no change)"
                                disabled={loading}
                                onChange={(values) => {
                                  const v = first(values);

                                  setSelections((prev) => ({
                                    ...prev,
                                    [m.source._id]: {
                                      sourceID: m.source._id,
                                      selectedID: v,
                                      merged: false,
                                    },
                                  }));
                                }}
                                maxDisplayItems={1}
                                options={[
                                  ...m.suggestions.map((m) =>
                                    getHitterOption(
                                      m,
                                      t('common.suggested').toString()
                                    )
                                  ),
                                  ...apiHitterOptions,
                                ]}
                                skipSort
                                optional
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <Button
                                className="btn-block"
                                variant={RADIX.BUTTON.VARIANT.SECONDARY}
                                color={
                                  model?.merged
                                    ? RADIX.COLOR.SUCCESS
                                    : undefined
                                }
                                disabled={loading}
                                onClick={async () => {
                                  if (loadingId) {
                                    NotifyHelper.warning({
                                      message_md:
                                        'Please wait for the previous request to finish first.',
                                    });
                                    return;
                                  }

                                  if (!model) {
                                    // nothing found, shouldn't trigger
                                    return;
                                  }

                                  if (model.merged) {
                                    // nothing to do
                                    return;
                                  }

                                  try {
                                    // hide the progress bar for one-at-a-time merges
                                    setProgress(-1);

                                    setLoadingId(m.source._id);

                                    const success = await applyMerge(model);

                                    if (success) {
                                      // mark entry as merged
                                      setSelections((prev) => ({
                                        ...prev,
                                        [m.source._id]: {
                                          sourceID: m.source._id,
                                          selectedID: model.selectedID,
                                          merged: true,
                                        },
                                      }));
                                    }
                                  } catch (e) {
                                    console.error(e);
                                  } finally {
                                    setLoadingId(undefined);
                                  }
                                }}
                              >
                                {t(
                                  model?.merged
                                    ? 'hitters.hitter-merged'
                                    : 'hitters.merge-hitter'
                                )}
                              </Button>
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table.Root>
                </ScrollArea>
              </Card>

              {progress >= 0 && <Progress value={progress} />}
            </Flex>
          }
          buttons={[
            {
              label: `Merge ${pending.length} Hitters`,
              disabled: !!loadingId,
              invisible: pending.length < 2, // just use the line button if there's only 1 entry
              onClick: async () => {
                try {
                  // reset/hide the progress bar to start
                  setProgress(-1);

                  const copyOfPending = clone(pending);

                  setLoadingId(BULK_ID);

                  // the bar should not render as empty
                  let nth = 1;
                  const total = copyOfPending.length + 1;

                  // e.g. for 10 items, working on the 1st item shows 1/11 % full
                  setProgress((nth / total) * 100);

                  for (const model of copyOfPending) {
                    const success = await applyMerge(model);

                    // await MiscHelper.sleep(3_000);

                    if (success) {
                      setSelections((prev) => ({
                        ...prev,
                        [model.sourceID]: {
                          ...model,
                          merged: true,
                        },
                      }));
                    }

                    nth++;

                    // update progress bar after each one finishes
                    setProgress((nth / total) * 100);
                  }
                } catch (e) {
                  console.error(e);
                } finally {
                  setLoadingId(undefined);
                }
              },
            },
          ]}
          onClose={() => {
            if (loadingId) {
              NotifyHelper.warning({
                message_md: 'Please wait for all tasks to finish first.',
              });
              return;
            }

            // force the hitter library to reload
            if (anyChanges) {
              setLastChanged(Date.now());
            }

            setOpen(false);
          }}
        />
      )}
    </ErrorBoundary>
  );
};
